// =====================================================
// LIB: Permissions Helpers
// Description: Fonctions utilitaires pour gérer les permissions
// =====================================================

import type { UserRole } from '@/types/user';
import type { EntityType, Action, EntityPermissions } from '@/types/permissions';
import { PERMISSIONS_CONFIG } from '@/types/permissions';

/**
 * Vérifie si un rôle a une permission donnée sur une entité
 */
export function hasPermission(userRole: UserRole, entity: EntityType, action: Action): boolean {
  const roleConfig = PERMISSIONS_CONFIG[userRole];
  if (!roleConfig) return false;

  // Map les entités aux clés de config
  const entityMap: Record<EntityType, keyof typeof roleConfig> = {
    contact: 'contacts',
    crew: 'crews',
    product: 'products',
    vehicle: 'vehicles',
    school: 'schools',
    association: 'associations',
    discount: 'discounts',
    form: 'forms',
    prospect: 'prospects',
    timeline: 'timeline',
    task: 'tasks',
    user: 'contacts',
  };

  const entityKey = entityMap[entity];
  if (!entityKey || !(entityKey in roleConfig)) return false;

  const entityPerms = roleConfig[entityKey] as EntityPermissions;

  // Map les actions aux clés de permissions
  const actionMap: Record<Action, keyof EntityPermissions> = {
    view: 'view',
    view_all: 'viewAll',
    create: 'create',
    update: 'update',
    delete: 'delete',
    import: 'import',
    export: 'export',
    delete_bulk: 'deleteBulk',
    manage_users: 'view', // Géré par canManageUsers
  };

  // Cas spécial pour manage_users
  if (action === 'manage_users') {
    return roleConfig.canManageUsers;
  }

  const permissionKey = actionMap[action];
  return entityPerms[permissionKey] || false;
}

/**
 * Filtre un tableau de données selon le rôle de l'utilisateur
 * Si l'utilisateur ne peut pas voir toutes les données, filtre par assigned_to ou created_by
 */
export function filterDataByRole<T extends Record<string, any>>(
  data: T[],
  userRole: UserRole,
  userId: string,
  entity: EntityType
): T[] {
  // Si l'utilisateur peut voir toutes les données, pas de filtre
  if (hasPermission(userRole, entity, 'view_all')) {
    return data;
  }

  // Sinon, filtrer par assigned_to ou created_by
  return data.filter((item) => {
    // Vérifier assigned_to en priorité
    if ('assigned_to' in item && item.assigned_to === userId) {
      return true;
    }

    // Vérifier created_by en second
    if ('created_by' in item && item.created_by === userId) {
      return true;
    }

    // Pour les entités qui n'ont pas d'assignation, autoriser la lecture
    // (comme products, schools, associations, discounts pour les Asso 4L)
    const noOwnershipEntities: EntityType[] = [
      'product',
      'school',
      'association',
      'discount',
      'form',
    ];
    if (noOwnershipEntities.includes(entity)) {
      return true;
    }

    return false;
  });
}

/**
 * Obtient toutes les permissions pour une entité donnée selon le rôle
 */
export function getPermissionsForEntity(
  entity: EntityType,
  userRole: UserRole
): EntityPermissions | null {
  const roleConfig = PERMISSIONS_CONFIG[userRole];
  if (!roleConfig) return null;

  const entityMap: Record<EntityType, keyof typeof roleConfig> = {
    contact: 'contacts',
    crew: 'crews',
    product: 'products',
    vehicle: 'vehicles',
    school: 'schools',
    association: 'associations',
    discount: 'discounts',
    form: 'forms',
    prospect: 'prospects',
    timeline: 'timeline',
    task: 'tasks',
    user: 'contacts',
  };

  const entityKey = entityMap[entity];
  if (!entityKey || !(entityKey in roleConfig)) return null;

  return roleConfig[entityKey] as EntityPermissions;
}

/**
 * Vérifie si un utilisateur peut accéder à une route
 */
export function canAccessRoute(route: string, userRole: UserRole): boolean {
  // Exception : sous-routes /admin également ouvertes aux admins (pas seulement
  // superadmin). Vérifiée AVANT le blocage blanket /admin ci-dessous, sinon
  // canAccessRoute redirige l'admin vers / et court-circuite le
  // `requiredRole: 'admin'` de la route. ⚠️ Doit rester aligné avec les deux
  // autres sites de gating front : NavItem.minRole === 'admin'
  // (src/lib/navigation/config.ts) et requiredRole: 'admin'
  // (src/components/Router.tsx). Cf. TICKET-CRM-07.
  const ADMIN_ACCESSIBLE_ADMIN_ROUTES = ['/admin/orphan-crew-numbers'];
  if (
    (userRole === 'admin' || userRole === 'superadmin') &&
    ADMIN_ACCESSIBLE_ADMIN_ROUTES.some(
      (prefix) => route === prefix || route.startsWith(prefix + '/')
    )
  ) {
    return true;
  }

  // Routes admin uniquement pour superadmin
  const adminRoutes = ['/admin/users', '/admin'];
  if (adminRoutes.some((r) => route.startsWith(r))) {
    return userRole === 'superadmin';
  }

  // Toutes les autres routes sont accessibles
  // Note: Les permissions réelles (créer/modifier/supprimer) sont gérées par le RLS
  // et les boutons UI sont masqués selon les permissions
  return true;
}

/**
 * Détermine si les données doivent être filtrées pour cet utilisateur
 */
export function shouldFilterData(userRole: UserRole, entity: EntityType): boolean {
  return !hasPermission(userRole, entity, 'view_all');
}

/**
 * Construit une clause WHERE Supabase pour filtrer les données selon le rôle
 * Utilisé pour les requêtes côté client
 */
export function buildFilterClause(
  userRole: UserRole,
  userId: string,
  entity: EntityType
): { field: string; value: string } | null {
  if (!shouldFilterData(userRole, entity)) {
    return null; // Pas de filtre nécessaire
  }

  // Retourner le champ sur lequel filtrer
  return {
    field: 'assigned_to',
    value: userId,
  };
}
